import React, { useEffect, useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Pagination,
  Button,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteDonation,
  fetchConsolidatedDonations,
} from "../../redux/donationSlice";
import {
  filterDonations,
  exportDonationsToExcel,
} from "./utils/DonationUtils";
import { normalizeReceiptNoForDisplay } from "../../utils/receiptNoFormatter";
// existing MUI imports-ல add பண்ணு
import { Backdrop, CircularProgress } from "@mui/material";
import Row from "./Row";
import Filter from "../Filter";
import ConfirmationDialog from "../ConfirmationDialog";
import HistoryModal from "../HistoryModal";
import SnackbarAlert from "../SnackBarAlert";
import AgGridLoader from "../AgGridLoader";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";
import Chip from "@mui/material/Chip";
import { logger } from "../../utils/logger";
import { useDebouncedLogger } from "../../hooks/useDebouncedLogger";
import { fetchPayPurpose } from "../../redux/paypurposeSlice";
import AlertMessage from "../AlertMessage";
import store from "../../redux/store";
import ExportFormatModal from "../ExportFormatModal";
const DonationList = ({ onEdit, onAppend, filterData }) => {
  const dispatch = useDispatch();
  const { consolidatedDonations = [], loading } = useSelector(
    (state) => state.donation || {}
  );
  const payPurposes = useSelector(
    (state) => state.paypurpose?.paypurpose || []
  );
  const [searchFilters, setSearchFilters] = useState({
    donorId: "",
    bookNo: "",
    remarks: "",
    globalSearch: "",
  });
  const [page, setPage] = useState(1);
  const [feedback, setFeedback] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedDonation, setSelectedDonation] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState([]);
  const { role } = useSelector((state) => state.auth || "user");
  const { user } = useSelector((state) => state.auth);
  const logSearch = useDebouncedLogger();
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);

  const donorsPerPage = 10;

  // Fetch data only if store is empty
  useEffect(() => {
    if (!consolidatedDonations || consolidatedDonations.length === 0) {
      // Show loading immediately
      setFeedback({
        open: true,
        message: "Loading donations... This may take a moment.",
        severity: "info",
      });

      dispatch(fetchConsolidatedDonations())
        .unwrap()
        .then((consolidatedData) => {
          logger.info("Consolidated donations fetched successfully", {
            page: "Donations",
            count: consolidatedData.length,
            username: user?.username,
            role: user?.role,
          });
        })
        .catch((err) => {
          logger.error("Error fetching consolidated donations", {
            page: "Donations",
            error: err,
            username: user?.username,
            role: user?.role,
          });
          setFeedback({
            open: true,
            message: "Error fetching donations",
            severity: "error",
          });
        });
    }
  }, [dispatch, consolidatedDonations, user]);

  useEffect(() => {
    // Only worth logging when this page was actually opened with a
    // pre-applied filter (e.g. navigated here from a specific transaction
    // elsewhere) — filterData is empty/undefined on a normal page visit,
    // and logging that empty case every time added no useful information.
    if (filterData?.transactionId) {
      logger.info("Donations page opened with pre-applied filter", {
        page: "Donations",
        transactionId: filterData.transactionId,
        username: user?.username,
        role: user?.role,
      });
      setSearchFilters((prev) => ({
        ...prev,
        globalSearch: "",
        donorId: "",
        bookNo: "",
        remarks: "",
        receiptNo: "",
        transactionId: filterData.transactionId,
      }));
    }
  }, [filterData, user]);

  // Fetch PayPurpose only if store is empty
  useEffect(() => {
    if (!payPurposes || payPurposes.length === 0) {
      dispatch(fetchPayPurpose());
    }
  }, [dispatch, payPurposes]);

  const handleDeleteDonation = (donation) => {
    // Check if this is a parent receipt with child receipts
    const parentReceipt = consolidatedDonations.find(
      (c) => c.parentDetails?._id === donation._id
    );

    if (
      parentReceipt &&
      parentReceipt.childReceipts &&
      parentReceipt.childReceipts.length > 0
    ) {
      setAlertMessage(
        "This parent receipt cannot be deleted because it has child receipts. Please delete the child receipts first."
      );
      setAlertOpen(true);
      return;
    }

    const hasPayPurpose = payPurposes.some(
      (p) =>
        p.donorId === donation.donorId &&
        p.transactionId === donation.transactionId
    );

    if (hasPayPurpose) {
      setAlertMessage(
        "This donation cannot be deleted because it has associated PayPurpose details."
      );
      setAlertOpen(true);
      return;
    }

    setSelectedDonation(donation);
    setOpenConfirm(true);
  };

  const filteredDonations = useMemo(
    () => filterDonations(consolidatedDonations, searchFilters),
    [consolidatedDonations, searchFilters]
  );

  // Flatten every individual donation record out of the receiptNo-grouped
  // consolidated response. Two unrelated donors can end up sharing a
  // receiptNo group (e.g. one legacy unprefixed receipt and one
  // year-prefixed receipt reducing to the same number) -- in that case the
  // backend exposes one record as parentDetails and the rest as
  // childReceipts, but neither is more "the" donor's record than the
  // other. Filtering/grouping must operate per-record, not just off
  // whichever record the group happened to pick as parentDetails, or a
  // donor whose receipt got buried as a "child" would be unsearchable.
  const allIndividualDonations = useMemo(() => {
    if (!Array.isArray(consolidatedDonations)) return [];
    const flat = [];
    consolidatedDonations.forEach((consolidated) => {
      if (consolidated?.parentDetails) {
        flat.push({
          ...consolidated.parentDetails,
          receiptNo: consolidated.receiptNo,
          displayReceiptNo: normalizeReceiptNoForDisplay(
            consolidated.receiptNo,
            consolidated.parentDetails.receiptDate
          ),
        });
      }
      (consolidated.childReceipts || []).forEach((child) => {
        flat.push({
          ...child,
          receiptNo: consolidated.receiptNo,
          displayReceiptNo: normalizeReceiptNoForDisplay(
            consolidated.receiptNo,
            child.receiptDate
          ),
        });
      });
    });
    return flat;
  }, [consolidatedDonations]);

  const filteredIndividualDonations = useMemo(() => {
    if (allIndividualDonations.length === 0) return [];

    // If no filters applied, return all records
    if (
      !searchFilters.donorId &&
      !searchFilters.bookNo &&
      !searchFilters.remarks &&
      !searchFilters.receiptNo &&
      !searchFilters.globalSearch &&
      !searchFilters.transactionId
    ) {
      return allIndividualDonations;
    }

    return allIndividualDonations.filter((record) => {
      // Apply EXACT MATCH filters
      if (
        searchFilters.transactionId &&
        record.transactionId?.toString() !==
          searchFilters.transactionId?.toString()
      ) {
        return false;
      }
      if (
        searchFilters.donorId &&
        record.donorId?.toString() !== searchFilters.donorId
      ) {
        return false;
      }
      if (
        searchFilters.bookNo &&
        record.bookNo?.toString() !== searchFilters.bookNo
      ) {
        return false;
      }
      if (
        searchFilters.receiptNo &&
        record.displayReceiptNo?.toString() !== searchFilters.receiptNo
      ) {
        return false;
      }
      if (
        searchFilters.remarks &&
        record.donationRemarks?.toString().toLowerCase() !==
          searchFilters.remarks.toLowerCase()
      ) {
        return false;
      }
      // Global search uses partial match for better UX
      if (searchFilters.globalSearch) {
        const search = searchFilters.globalSearch.toLowerCase();
        return (
          record.donorId?.toString().toLowerCase().includes(search) ||
          record.firstName?.toLowerCase().includes(search) ||
          record.lastName?.toLowerCase().includes(search) ||
          record.mobile1?.toString().includes(search) ||
          record.mobile2?.toString().includes(search) ||
          record.city?.toLowerCase().includes(search) ||
          record.displayReceiptNo?.toString().toLowerCase().includes(search)
        );
      }
      return true;
    });
  }, [allIndividualDonations, searchFilters]);

  // Group by donor - one row per donor with all their receipts
  const groupedByDonor = useMemo(() => {
    const donorMap = new Map();

    filteredIndividualDonations.forEach((record) => {
      const donorId = record.donorId;
      if (!donorId) return;

      if (!donorMap.has(donorId)) {
        donorMap.set(donorId, {
          donorId: donorId,
          donorDetails: record,
          receipts: [],
        });
      }

      donorMap.get(donorId).receipts.push(record);
    });

    return Array.from(donorMap.values());
  }, [filteredIndividualDonations]);

  // Count total individual donations
  const totalIndividualDonations = useMemo(() => {
    if (!Array.isArray(consolidatedDonations)) return 0;

    // Use donationCount from backend (already includes parent + children)
    return consolidatedDonations.reduce((sum, consolidated) => {
      return sum + (consolidated.donationCount || 0);
    }, 0);
  }, [consolidatedDonations]);

  const paginatedDonors = useMemo(() => {
    const start = (page - 1) * donorsPerPage;
    return groupedByDonor.slice(start, start + donorsPerPage);
  }, [groupedByDonor, page]);

  const handleFilterChange = (filters) => {
    setSearchFilters(filters);
    setPage(1);
    // Debounced — logs the settled filter state ~700ms after the user
    // stops typing, instead of once per keystroke.
    logSearch("donationFilter", "Donation table search applied", {
      page: "Donations",
      filtersApplied: filters,
      username: user?.username,
      role: user?.role,
    });
  };

  const handleAddDonation = (data) => {
    const donorDetails = {
      donorId: data.donorId,
      firstName: data.firstName,
      lastName: data.lastName,
      mobile1: data.mobile1,
      city: data.city,
    };
    logger.info("Add donation clicked", {
      page: "Donations",
      donorId: data.donorId,
      username: user?.username,
      role: user?.role,
    });
    onAppend(donorDetails);
  };

  const handleEdit = (data) => {
    logger.info("Edit donation clicked", {
      page: "Donations",
      donorId: data.donorId,
      transactionId: data.transactionId,
      username: user?.username,
      role: user?.role,
    });
    onEdit(data);
  };

  const handleDeleteConfirm = () => {
    if (selectedDonation) {
      dispatch(deleteDonation(selectedDonation._id))
        .unwrap()
        .then(() => {
          setFeedback({
            open: true,
            message: "Donation deleted successfully",
            severity: "success",
          });
          logger.info("Donation deleted", {
            page: "Donations",
            donorId: selectedDonation.donorId,
            transactionId: selectedDonation.transactionId,
            username: user?.username,
            role: user?.role,
          });
        })
        .catch((err) => {
          setFeedback({
            open: true,
            message: "Error deleting donation",
            severity: "error",
          });
          logger.error("Error deleting donation", {
            page: "Donations",
            error: err,
            donorId: selectedDonation.donorId,
            transactionId: selectedDonation.transactionId,
            username: user?.username,
            role: user?.role,
          });
        });
    }
    setOpenConfirm(false);
    setSelectedDonation(null);
  };

  const handleViewHistory = (donation) => {
    if (donation.updateHistory && donation.updateHistory.length > 0) {
      setSelectedHistory(donation.updateHistory);
      setHistoryOpen(true);
      logger.info("View updated donation history modal open", {
        page: "Donations",
        donorId: donation.donorId,
        transactionId: donation.transactionId,
        username: user?.username,
        role: user?.role,
      });
    } else {
      setFeedback({
        open: true,
        message: "No history available for this donation",
        severity: "info",
      });
      logger.info("No donation history available", {
        page: "Donations",
        donorId: donation.donorId,
        transactionId: donation.transactionId,
        username: user?.username,
        role: user?.role,
      });
    }
  };

  const handleCloseHistory = () => {
    setHistoryOpen(false);
    setSelectedHistory([]);
    logger.info("View updated donation history modal close", {
      page: "Donations",
      username: user?.username,
      role: user?.role,
    });
  };

  const handleCloseFeedback = () => {
    setFeedback({ ...feedback, open: false });
  };

  const handleClearTransactionFilter = () => {
    setSearchFilters((prev) => ({ ...prev, transactionId: "" }));
    setPage(1);
  };
  const [exportModalOpen, setExportModalOpen] = useState(false);

const handleExcelExport = () => {
  exportDonationsToExcel(allIndividualDonations, user);
};

const handlePdfExport = async () => {
    setPdfLoading(true);
  try {
    const BaseUrl = process.env.REACT_APP_BASE_URL;
    const state = store.getState();
    const token = state?.auth?.token || sessionStorage.getItem("accessToken");

    const response = await fetch(`${BaseUrl}api/donations/export-pdf`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) throw new Error("Failed to generate PDF");

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Donations_export_${Date.now()}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    alert("Error downloading PDF: " + err.message);
  }
  finally{
     setPdfLoading(false);
  }
};

  return (
    <>
      <Filter
        onFilterChange={handleFilterChange}
        values={searchFilters}
        fields={["donorId", "bookNo", "remarks", "receiptNo"]}
      />
      {searchFilters.transactionId && (
        <Box sx={{ mb: 1, mt: 0.5 }}>
          <Chip
            label={`Transaction ID: ${searchFilters.transactionId}`}
            onDelete={handleClearTransactionFilter}
            size="small"
            sx={{
              borderRadius: "16px",
              border: "1px solid #000",
              backgroundColor: "#fff",
              color: "#000",
              fontWeight: 500,
              "& .MuiChip-deleteIcon": {
                color: "red",
                "&:hover": { color: "#ff0000" },
              },
            }}
          />
        </Box>
      )}
      <Box display="flex" justifyContent="end">
        {role === "superadmin" || role === "admin1" || role === "admin2" ? (
  <Button onClick={handleExcelExport} variant="contained" color="primary">
    <DriveFileMoveIcon /> Export
  </Button>
) : null}
      </Box>
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: 782,
          overflowY: "auto",
          overflowX: "hidden",
          width: "100%",
        }}
      >
        <Table stickyHeader sx={{ width: "100%", tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "5%" }} />
              <TableCell sx={{ width: "12%" }}>Actions</TableCell>
              <TableCell sx={{ width: "12%" }}>Donor ID</TableCell>
              <TableCell sx={{ width: "20%" }}>First Name</TableCell>
              <TableCell sx={{ width: "18%" }}>Mobile Number 1</TableCell>
              <TableCell sx={{ width: "18%" }}>Mobile Number 2</TableCell>
              <TableCell sx={{ width: "15%" }}>City</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="100%"
                  >
                    <AgGridLoader loadingMessage="Fetching Donations, please wait..." />
                  </Box>
                </TableCell>
              </TableRow>
            ) : paginatedDonors.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="100%"
                  >
                    <Typography variant="h6">No Donations Found</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              paginatedDonors.map((donorGroup, index) => {
                // Safety check
                if (!donorGroup || !donorGroup.donorDetails) {
                  return null;
                }

                // Each entry in donorGroup.receipts is already an
                // individual donation record belonging to this donor.
                const allDonations = donorGroup.receipts;

                // Calculate total amount for all receipts
                const totalAmount = donorGroup.receipts.reduce(
                  (sum, r) => sum + Number(r.amount || 0),
                  0
                );

                return (
                  <Row
                    key={`${donorGroup.donorId}-${index}`}
                    donor={donorGroup.donorDetails}
                    allDonations={allDonations}
                    payPurposes={payPurposes}
                    donationCount={allDonations.length}
                    totalAmount={totalAmount}
                    onEdit={handleEdit}
                    onDelete={handleDeleteDonation}
                    onView={handleViewHistory}
                    onAppend={handleAddDonation}
                    role={role}
                  />
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mt={2}
      >
        <Typography variant="body2" color="textSecondary">
          {`${(page - 1) * donorsPerPage + 1} to ${Math.min(
            page * donorsPerPage,
            groupedByDonor.length
          )} of ${groupedByDonor.length} | Page ${page} of ${Math.ceil(
            groupedByDonor.length / donorsPerPage
          )}`}
        </Typography>

        <Pagination
          count={Math.ceil(groupedByDonor.length / donorsPerPage)}
          page={page}
          onChange={(e, value) => setPage(value)}
          color="primary"
          shape="rounded"
        />

        <Typography variant="body2" color="textSecondary">
          Total Donations: {totalIndividualDonations}
        </Typography>
      </Box>

      <HistoryModal
        title="Donation Updated History"
        open={historyOpen}
        onClose={handleCloseHistory}
        history={selectedHistory}
      />
      <ConfirmationDialog
        open={openConfirm}
        title="Confirm Deletion"
        message="Are you sure you want to delete this donation?"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setOpenConfirm(false)}
      />
      <AlertMessage
        title="Donation Delete Alert"
        open={alertOpen}
        setOpen={setAlertOpen}
        message={alertMessage}
      />

      <SnackbarAlert feedback={feedback} onClose={handleCloseFeedback} />
    </>
  );
};
export default DonationList;
